import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDashboard, reportStats, getTeam } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { PageHeader, Card, Loading } from "@/components/Shared";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import {
  TrendingUp, Clock, CheckCircle2, AlertTriangle, FileCheck, Wallet, Plus, FileText, ArrowUpRight, RefreshCw, Bell, UsersRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell,
} from "recharts";

const STAT_COLORS = {
  revenue: { icon: TrendingUp, bg: "bg-emerald-50", fg: "text-emerald-600" },
  outstanding: { icon: Clock, bg: "bg-amber-50", fg: "text-amber-600" },
  paid: { icon: CheckCircle2, bg: "bg-blue-50", fg: "text-blue-600" },
  overdue: { icon: AlertTriangle, bg: "bg-rose-50", fg: "text-rose-600" },
};

const PIE = { paid: "#10B981", overdue: "#EF4444", outstanding: "#F59E0B", draft: "#94A3B8" };

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [rstats, setRstats] = useState(null);
  const [team, setTeam] = useState(null);
  const canTeam = user?.role === "admin" || user?.is_owner || (Array.isArray(user?.permissions) && user.permissions.includes("team.view"));

  useEffect(() => {
    getDashboard().then(setData).catch(() => {});
    reportStats().then(setRstats).catch(() => {});
    if (canTeam) getTeam().then(setTeam).catch(() => {});
  }, [canTeam]);

  if (!data) return <Loading />;

  const stats = [
    { key: "revenue", label: "Total Revenue", value: money(data.revenue), sub: `${data.payments_count} payments` },
    { key: "outstanding", label: "Outstanding", value: money(data.outstanding), sub: `${data.total_invoices} invoices` },
    { key: "paid", label: "Paid Invoices", value: data.paid_invoices, sub: "fully settled" },
    { key: "overdue", label: "Overdue", value: data.overdue_invoices, sub: "needs attention" },
  ];

  const pieData = Object.entries(data.status_breakdown)
    .filter(([, v]) => v > 0)
    .map(([name, value]) => ({ name, value }));

  return (
    <div>
      <PageHeader title={`Welcome back, ${user?.name?.split(" ")[0] || "there"}`} subtitle="Here's how your business is performing today.">
        {team && (
          <button
            onClick={() => nav("/team")}
            className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white hover:border-[#D4AF37] transition-colors text-sm"
            data-testid="dashboard-team-capacity"
            title={team.remaining <= 0 ? "Team member limit reached" : `${team.remaining} slot${team.remaining === 1 ? "" : "s"} remaining`}
          >
            <UsersRound className="w-4 h-4 text-[#1E7A6B]" />
            <span className="font-semibold text-[#0B132B]">{team.used} / {team.limit}</span>
            <span className="text-slate-400">team</span>
          </button>
        )}
        <Button onClick={() => nav("/invoices/new")} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="dashboard-new-invoice">
          <Plus className="w-4 h-4 mr-1.5" /> New Invoice
        </Button>
      </PageHeader>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map((s) => {
          const c = STAT_COLORS[s.key];
          return (
            <Card key={s.key} className="p-5" data-testid={`stat-${s.key}`}>
              <div className="flex items-start justify-between">
                <div className={`w-10 h-10 rounded-lg ${c.bg} ${c.fg} flex items-center justify-center`}>
                  <c.icon className="w-5 h-5" />
                </div>
              </div>
              <div className="mt-4 font-display text-2xl font-bold text-[#0B132B]">{s.value}</div>
              <div className="text-xs text-slate-500 mt-0.5">{s.label} · {s.sub}</div>
            </Card>
          );
        })}
      </div>

      {/* recurring + reminders */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-5" data-testid="recurring-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><span className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center"><RefreshCw className="w-4 h-4" /></span><h3 className="font-display font-semibold text-[#0B132B]">Recurring Invoices</h3></div>
            <button onClick={() => nav("/recurring")} className="text-xs text-[#0B132B] font-medium hover:text-[#D4AF37] flex items-center gap-1" data-testid="view-recurring">Manage <ArrowUpRight className="w-3.5 h-3.5" /></button>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div><div className="font-display text-2xl font-bold text-[#0B132B]" data-testid="recurring-active">{data.recurring?.active ?? 0}</div><div className="text-xs text-slate-500">Active</div></div>
            <div><div className="font-display text-2xl font-bold text-[#0B132B]" data-testid="recurring-generated-month">{data.recurring?.generated_this_month ?? 0}</div><div className="text-xs text-slate-500">This month</div></div>
            <div><div className="font-display text-sm font-bold text-[#0B132B] mt-1.5" data-testid="recurring-next">{data.recurring?.next_scheduled ? fmtDate(data.recurring.next_scheduled) : "—"}</div><div className="text-xs text-slate-500">Next invoice</div></div>
          </div>
        </Card>
        <Card className="p-5" data-testid="reminders-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><span className="w-9 h-9 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center"><Bell className="w-4 h-4" /></span><h3 className="font-display font-semibold text-[#0B132B]">Overdue Reminders</h3></div>
            <button onClick={() => nav("/invoices")} className="text-xs text-[#0B132B] font-medium hover:text-[#D4AF37] flex items-center gap-1" data-testid="view-overdue">View <ArrowUpRight className="w-3.5 h-3.5" /></button>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div><div className="font-display text-2xl font-bold text-rose-600" data-testid="reminders-overdue">{data.reminders?.overdue_invoices ?? 0}</div><div className="text-xs text-slate-500">Overdue</div></div>
            <div><div className="font-display text-2xl font-bold text-emerald-600" data-testid="reminders-sent">{data.reminders?.sent ?? 0}</div><div className="text-xs text-slate-500">Sent</div></div>
            <div><div className="font-display text-2xl font-bold text-amber-600" data-testid="reminders-pending">{data.reminders?.pending ?? 0}</div><div className="text-xs text-slate-500">Pending</div></div>
          </div>
        </Card>
        <Card className="p-5" data-testid="reports-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><span className="w-9 h-9 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center"><FileText className="w-4 h-4" /></span><h3 className="font-display font-semibold text-[#0B132B]">Work Reports</h3></div>
            <button onClick={() => nav("/work-reports")} className="text-xs text-[#0B132B] font-medium hover:text-[#D4AF37] flex items-center gap-1" data-testid="view-work-reports">Open <ArrowUpRight className="w-3.5 h-3.5" /></button>
          </div>
          <div className="grid grid-cols-4 gap-2 text-center">
            <div><div className="font-display text-xl font-bold text-[#0B132B]" data-testid="reports-month">{rstats?.this_month ?? 0}</div><div className="text-[10px] text-slate-500">Month</div></div>
            <div><div className="font-display text-xl font-bold text-emerald-600" data-testid="reports-completed">{rstats?.completed ?? 0}</div><div className="text-[10px] text-slate-500">Done</div></div>
            <div><div className="font-display text-xl font-bold text-slate-500" data-testid="reports-draft">{rstats?.draft ?? 0}</div><div className="text-[10px] text-slate-500">Draft</div></div>
            <div><div className="font-display text-xl font-bold text-[#D4AF37]" data-testid="reports-sent">{rstats?.sent ?? 0}</div><div className="text-[10px] text-slate-500">Sent</div></div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* revenue chart */}        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-[#0B132B]">Revenue (last 6 months)</h3>
            <span className="text-xs text-slate-400 uppercase tracking-wide">ZAR</span>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.monthly_revenue}>
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={40} />
              <Tooltip formatter={(v) => money(v)} cursor={{ fill: "#F1F5F9" }} contentStyle={{ borderRadius: 10, border: "1px solid #E2E8F0", fontSize: 13 }} />
              <Bar dataKey="revenue" fill="#0B132B" radius={[6, 6, 0, 0]} maxBarSize={44} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* status pie + quick links */}
        <Card className="p-5">
          <h3 className="font-display font-semibold text-[#0B132B] mb-2">Invoice Status</h3>
          {pieData.length ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={72} paddingAngle={3}>
                  {pieData.map((e) => <Cell key={e.name} fill={PIE[e.name] || "#94A3B8"} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #E2E8F0", fontSize: 13, textTransform: "capitalize" }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[180px] flex items-center justify-center text-sm text-slate-400">No invoices yet</div>
          )}
          <div className="flex flex-wrap gap-2 mt-2">
            {pieData.map((e) => (
              <span key={e.name} className="flex items-center gap-1.5 text-xs text-slate-600 capitalize">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: PIE[e.name] }} /> {e.name} ({e.value})
              </span>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* recent invoices */}
        <Card className="lg:col-span-2 overflow-hidden">
          <div className="flex items-center justify-between p-5 border-b border-slate-100">
            <h3 className="font-display font-semibold text-[#0B132B]">Recent Invoices</h3>
            <button onClick={() => nav("/invoices")} className="text-xs text-[#0B132B] font-medium hover:text-[#D4AF37] flex items-center gap-1" data-testid="view-all-invoices">
              View all <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </div>
          {data.recent_invoices.length ? (
            <div className="divide-y divide-slate-100">
              {data.recent_invoices.map((i) => (
                <button key={i.id} onClick={() => nav(`/invoices/${i.id}`)} className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 transition-colors text-left" data-testid={`recent-invoice-${i.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-[#F1F5F9] flex items-center justify-center text-[#0B132B]"><FileText className="w-4 h-4" /></div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#0B132B] truncate">{i.number}</div>
                      <div className="text-xs text-slate-500 truncate">{i.customer?.name || "No customer"} · {fmtDate(i.issue_date)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm font-semibold text-[#0B132B]">{money(i.total)}</span>
                    <StatusBadge status={i.display_status} />
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="p-10 text-center text-sm text-slate-400">No invoices yet</div>
          )}
        </Card>

        {/* activity */}
        <Card className="p-5">
          <h3 className="font-display font-semibold text-[#0B132B] mb-4">Recent Activity</h3>
          {data.recent_activity.length ? (
            <div className="space-y-4">
              {data.recent_activity.map((a, idx) => (
                <div key={idx} className="flex items-start gap-3" data-testid={`activity-${idx}`}>
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${a.type === "payment" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
                    {a.type === "payment" ? <Wallet className="w-4 h-4" /> : <FileCheck className="w-4 h-4" />}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm text-[#0B132B] truncate">{a.text}</div>
                    <div className="text-xs text-slate-400">{fmtDate(a.date)} · {money(a.amount)}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-slate-400 py-8 text-center">No activity yet</div>
          )}
        </Card>
      </div>
    </div>
  );
}
